import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { normalizeMinutes } from "./flightTimingUtils";
import { normalizeAirportCode } from "./routeUtils";

dayjs.extend(utc);

export const ADD_FLIGHT_INSERTION_MODES = {
    END: "end",
    BEFORE: "before",
    AFTER: "after",
};

export const DEFAULT_BLOCK_TIME_MINUTES = 60;
const FALLBACK_PREFERRED_TIME = "08:00";

export function getAddFlightRouteBlockTimeMinutes(route) {
    return normalizeMinutes(route?.block_time_minutes);
}

export function getAirportMinGroundTimeMinutes(airportCode, airportConstraintsMap = {}) {
    const normalizedAirport = normalizeAirportCode(airportCode);
    const constraint = airportConstraintsMap?.[normalizedAirport] || airportConstraintsMap?.[airportCode] || null;
    if (!constraint) {
        return null;
    }

    return normalizeMinutes(
        constraint?.turnaround_time
        ?? constraint?.min_ground_time_minutes
        ?? constraint?.minimum_ground_time_minutes,
    );
}

function isActiveFlight(flight) {
    return flight && !flight.is_deleted && String(flight.change_status || "").toUpperCase() !== "DELETED";
}

function getComparableFlights(flights = [], aircraftReg) {
    const normalizedAircraftReg = String(aircraftReg || "").trim().toUpperCase();
    return flights.filter((flight) => {
        if (!isActiveFlight(flight)) {
            return false;
        }
        if (!normalizedAircraftReg) {
            return true;
        }
        return String(flight?.aircraft_reg || "").trim().toUpperCase() === normalizedAircraftReg;
    });
}

function findAnchorFlight(flights = [], insertion = {}) {
    const anchorFlight = insertion.anchorFlight || insertion.anchor_flight;
    if (anchorFlight) {
        const anchorFlightId = anchorFlight.id ?? anchorFlight.flight_id;
        if (anchorFlightId !== undefined && anchorFlightId !== null) {
            return flights.find((flight) => String(flight?.id || flight?.flight_id || "") === String(anchorFlightId)) || null;
        }
        return flights.includes(anchorFlight) ? anchorFlight : null;
    }

    const anchorFlightId = insertion.anchorFlightId ?? insertion.anchor_flight_id;
    if (!anchorFlightId) {
        return null;
    }

    return flights.find((flight) => String(flight?.id || "") === String(anchorFlightId)) || null;
}

function getPreferredDemand(draft, route) {
    const routeDep = normalizeAirportCode(route?.departure_airport);
    const routeArr = normalizeAirportCode(route?.arrival_airport);
    const demands = Array.isArray(draft?.transport_demands) ? draft.transport_demands : [];

    return demands.find((demand) => {
        const demandDep = normalizeAirportCode(demand?.departure_airport || demand?.origin || demand?.origin_airport || demand?.dep);
        const demandArr = normalizeAirportCode(demand?.arrival_airport || demand?.destination || demand?.destination_airport || demand?.arr);
        return demandDep === routeDep && (!demandArr || demandArr === routeArr);
    }) || demands.find((demand) => {
        const demandDep = normalizeAirportCode(demand?.departure_airport || demand?.origin || demand?.origin_airport || demand?.dep);
        return demandDep === routeDep;
    }) || null;
}

function getBaseDate(draft, demand, now) {
    return dayjs.utc(
        demand?.preferred_date
        || demand?.date
        || demand?.service_date
        || draft?.period_start
        || now,
    ).startOf("day");
}

function buildDefaultStdFromDemand(draft, route, now) {
    const demand = getPreferredDemand(draft, route);
    const preferredTime = String(demand?.preferred_time || FALLBACK_PREFERRED_TIME).trim();
    const parsedDateTime = dayjs.utc(preferredTime);
    if (preferredTime.includes("-") && parsedDateTime.isValid()) {
        return parsedDateTime.second(0).millisecond(0);
    }

    const timeMatch = preferredTime.match(/^(\d{1,2}):(\d{2})$/);
    const baseDate = getBaseDate(draft, demand, now);
    if (timeMatch) {
        return baseDate.hour(Number(timeMatch[1])).minute(Number(timeMatch[2])).second(0).millisecond(0);
    }

    return baseDate.hour(8).minute(0).second(0).millisecond(0);
}

function buildTimingResult(stdValue, route, scenario, warnings = []) {
    const blockTimeMinutes = getAddFlightRouteBlockTimeMinutes(route);
    if (blockTimeMinutes === null) {
        return null;
    }

    const std = dayjs.utc(stdValue);
    if (!std.isValid()) {
        return buildTimingResult(buildDefaultStdFromDemand(null, route, new Date()), route, "fallback", warnings);
    }

    return {
        std: std.toISOString(),
        sta: std.add(blockTimeMinutes, "minute").toISOString(),
        blockTimeMinutes,
        scenario,
        warnings,
    };
}

function maybeAddContinuityWarning(warnings, previousArrival, nextDeparture) {
    const previousArr = normalizeAirportCode(previousArrival);
    const nextDep = normalizeAirportCode(nextDeparture);
    if (previousArr && nextDep && previousArr !== nextDep) {
        warnings.push(`机场不连续：${previousArr} -> ${nextDep}`);
    }
    return warnings;
}

export function inferAddFlightTimes({
    flights = [],
    route,
    insertion = {},
    aircraftReg,
    draft,
    airportConstraintsMap = {},
    now = new Date(),
} = {}) {
    const routeDep = normalizeAirportCode(route?.departure_airport);
    const routeArr = normalizeAirportCode(route?.arrival_airport);
    const blockTimeMinutes = getAddFlightRouteBlockTimeMinutes(route);
    if (blockTimeMinutes === null) {
        return null;
    }

    const comparableFlights = getComparableFlights(flights, aircraftReg);
    const mode = insertion?.mode || ADD_FLIGHT_INSERTION_MODES.END;

    if (!comparableFlights.length) {
        return buildTimingResult(buildDefaultStdFromDemand(draft, route, now), route, "empty");
    }

    const anchorFlight = findAnchorFlight(comparableFlights, insertion);

    if (mode === ADD_FLIGHT_INSERTION_MODES.BEFORE && anchorFlight?.std) {
        const anchorStd = dayjs.utc(anchorFlight.std);
        const minGroundTimeMinutes = getAirportMinGroundTimeMinutes(routeArr, airportConstraintsMap);
        if (minGroundTimeMinutes === null) {
            return null;
        }

        const warnings = maybeAddContinuityWarning([], routeArr, anchorFlight.departure_airport);
        return buildTimingResult(anchorStd.subtract(blockTimeMinutes + minGroundTimeMinutes, "minute"), route, "before", warnings);
    }

    if (mode === ADD_FLIGHT_INSERTION_MODES.AFTER && (anchorFlight?.sta || anchorFlight?.std)) {
        const anchorSta = dayjs.utc(anchorFlight.sta || anchorFlight.std);
        const minGroundTimeMinutes = getAirportMinGroundTimeMinutes(anchorFlight.arrival_airport, airportConstraintsMap);
        if (minGroundTimeMinutes === null) {
            return null;
        }

        const warnings = maybeAddContinuityWarning([], anchorFlight.arrival_airport, routeDep);
        return buildTimingResult(anchorSta.add(minGroundTimeMinutes, "minute"), route, "after", warnings);
    }

    const lastFlight = comparableFlights[comparableFlights.length - 1];
    const lastSta = dayjs.utc(lastFlight?.sta || lastFlight?.std);
    const minGroundTimeMinutes = getAirportMinGroundTimeMinutes(lastFlight?.arrival_airport, airportConstraintsMap);
    if (minGroundTimeMinutes === null) {
        return null;
    }

    const warnings = maybeAddContinuityWarning([], lastFlight?.arrival_airport, routeDep);
    return buildTimingResult(lastSta.add(minGroundTimeMinutes, "minute"), route, "end", warnings);
}

const addFlightTimeUtils = {
    ADD_FLIGHT_INSERTION_MODES,
    getAddFlightRouteBlockTimeMinutes,
    getAirportMinGroundTimeMinutes,
    inferAddFlightTimes,
};

export default addFlightTimeUtils;
